import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import { iif, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, share, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { State } from 'src/app/store';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { Residence } from 'src/app/residence/residence.model';
import { ProfileField, Permissions } from '@bciti/types';
import { accessSelector, profileOptionsSelector } from 'src/app/global/store/global.selectors';
import { authPermissionsSelector } from 'src/app/auth/store/auth.selectors';
import { GetAccessAction } from 'src/app/global/store/global.actions';
import { formatDateOnly } from 'src/app/global/utils/date/date.utils';
import { UserService } from '../../service/user.abstract.service';
import { SearchUsersAction, SetUserSearchActiveForm, SetUserSearchAdvancedForm, SetUserSearchPageOptionsAction, SetUserSearchQuickFilters, SetUserSearchQuickForm } from '../../store/user.actions';
import {
    userSearchActiveFormSelector,
    userSearchAdvancedFormSelector,
    userSearchPageOptionsSelector,
    userSearchQuickFiltersSelector,
    userSearchQuickFormSelector,
    userSearchTotalSelector,
    userSearchUsersSelector,
} from '../../store/user.selectors';
import { AvailableUserSearchForms, CardStatus, CardType, Roles } from '../../user.model';
import { UserUtilService } from '../../util/user-util.service';
import { MoreFiltersDialogComponent } from '../../dialogs/more-filters-dialog/more-filters-dialog.component';

@Component({
    selector: 'app-user-records',
    templateUrl: './user-records.component.html',
    styleUrls: ['./user-records.component.scss'],
})
export class UserRecordsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();
    private formValueChange$ = new Subject<any>();

    public CardType = CardType;

    public maxExportResults = 1000;

    public t: any = false;

    public advancedSearchForm: FormGroup;

    public listData = [];

    public quickSearchForm: FormGroup;

    public pageSubject: Subject<any> = new Subject();

    public users: any[] = [];

    public userLength = 0;

    public pageOptions: any;

    public userColumns: any[];

    public searchResultsType = '';

    public searchResultsToExport: any[] = [];

    public roleOptions = [];

    public confirmedOptions = [];

    public cardStatusOptions = [];

    public isHiddenAdvancedSearchButton = false;

    public isHiddenAdvancedSearchForm = true;

    public loading = false;

    public tableReloading = false;

    public hiddenFields: ProfileField[] = [];

    public profileField = ProfileField;

    // Permission flags
    public hasProfilePermission = false;
    public hasFamilyPermission = false;

    // Toggle button states
    public cardsToApproveActive = false;
    public familyLinksToApproveActive = false;
    public residencesToApproveActive = false;

    // Pending counts for badges
    public pendingCardsCount = 0;
    public pendingFamilyLinksCount = 0;
    public pendingResidencesCount = 0;

    // Residence data for table enrichment
    public residenceMap = new Map<string, Residence[]>();

    // More filters state
    public moreFiltersData: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
        private store: Store<State>,
        private translateService: TranslateService,
        private userService: UserService,
        private residenceService: ResidenceService,
        @Inject('Window') private window: Window,
        private userUtil: UserUtilService,
    ) {}

    public ngOnInit(): void {
        this.init();

        this.translateService.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.tableReloading = true;
                    this.setupTableColumn();
                    setTimeout(() => {
                        this.tableReloading = false;
                    }, 1000);
                }),
            )
            .subscribe();

        // Setup debounced form changes
        this.formValueChange$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(500),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                tap(() => this.triggerSearch()),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public init(): void {
        this.store
            .select(profileOptionsSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((profileOptions) => {
                    this.hiddenFields = profileOptions?.hiddenFields ?? [];
                }),
            )
            .subscribe();

        this.store
            .select(accessSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((accesses) => {
                    if (accesses.length === 0) {
                        this.store.dispatch(GetAccessAction());
                        return;
                    }

                    this.roleOptions = [];

                    accesses.forEach((access) =>
                        this.roleOptions.push({
                            label: access.title,
                            value: access?.type.toLowerCase(),
                        }),
                    );
                }),
            )
            .subscribe();

        this.store
            .select(authPermissionsSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((permissions) => {
                    this.hasProfilePermission = permissions?.includes(Permissions.PROFILE) ?? false;
                    this.hasFamilyPermission = permissions?.includes(Permissions.FAMILY) ?? false;
                }),
            )
            .subscribe();

        this.setupTotalRecordSub();
        this.setupPageOptionsSub();
        this.setupForm();
        this.removeHiddenFields();
        this.setupTableColumn();
        this.setupUsersSub();
        this.setupPageSubjectSub();
        this.setupDropdowns();
        this.fetchPendingCounts();
        this.restoreQuickFiltersFromStore();
    }

    private restoreQuickFiltersFromStore(): void {
        this.store
            .select(userSearchQuickFiltersSelector)
            .pipe(
                take(1),
                tap((quickFilters) => {
                    if (quickFilters) {
                        this.cardsToApproveActive = quickFilters.cardsToApproveActive ?? false;
                        this.familyLinksToApproveActive = quickFilters.familyLinksToApproveActive ?? false;
                        this.residencesToApproveActive = quickFilters.residencesToApproveActive ?? false;
                        // moreFiltersData carries the pendingCardApproval / familyStatus / residenceStatus quick filters
                        this.moreFiltersData = { ...(quickFilters.moreFiltersData ?? {}) };
                    }
                }),
            )
            .subscribe();
    }

    get quickSearch(): AbstractControl {
        return this.quickSearchForm.get('search');
    }

    get firstName(): AbstractControl {
        return this.advancedSearchForm.get('firstName');
    }

    get lastName(): AbstractControl {
        return this.advancedSearchForm.get('lastName');
    }

    get email(): AbstractControl {
        return this.advancedSearchForm.get('email');
    }

    get birthDate(): AbstractControl {
        return this.advancedSearchForm.get('birthDate');
    }

    get phone(): AbstractControl {
        return this.advancedSearchForm.get('phone');
    }

    get role(): AbstractControl {
        return this.advancedSearchForm.get('role');
    }

    get confirmed(): AbstractControl {
        return this.advancedSearchForm.get('confirmed');
    }

    get number(): AbstractControl {
        return this.advancedSearchForm.get('number');
    }

    get streetName(): AbstractControl {
        return this.advancedSearchForm.get('streetName');
    }

    get postCode(): AbstractControl {
        return this.advancedSearchForm.get('postCode');
    }

    get cardStatus(): AbstractControl {
        return this.advancedSearchForm.get('cardStatus');
    }

    get cardCode(): AbstractControl {
        return this.advancedSearchForm.get('cardCode');
    }

    public toggleCardsToApprove(): void {
        this.cardsToApproveActive = !this.cardsToApproveActive;

        if (this.cardsToApproveActive) {
            // Dedicated filter: card status PROCESSING OR a renewal request in PROCESSING.
            // Kept out of the cardStatus control so the "more filters" status dropdown stays strict.
            this.moreFiltersData = { ...this.moreFiltersData, pendingCardApproval: 'true' };
        } else {
            const { pendingCardApproval, ...rest } = this.moreFiltersData;
            this.moreFiltersData = rest;
        }

        this.triggerSearch();
    }

    public toggleFamilyLinksToApprove(): void {
        this.familyLinksToApproveActive = !this.familyLinksToApproveActive;

        if (this.familyLinksToApproveActive) {
            // Add familyStatus filter for pending approvals - create new object to avoid mutation
            this.moreFiltersData = { ...this.moreFiltersData, familyStatus: 'false' };
        } else {
            // Remove familyStatus - create new object without the property
            const { familyStatus, ...rest } = this.moreFiltersData;
            this.moreFiltersData = rest;
        }

        this.triggerSearch();
    }

    public toggleResidencesToApprove(): void {
        this.residencesToApproveActive = !this.residencesToApproveActive;

        if (this.residencesToApproveActive) {
            this.moreFiltersData = { ...this.moreFiltersData, residenceStatus: 'PENDING' };
        } else {
            const { residenceStatus, ...rest } = this.moreFiltersData;
            this.moreFiltersData = rest;
        }

        this.triggerSearch();
    }

    public resetFilters(): void {
        this.advancedSearchForm.reset();
        this.moreFiltersData = {};
        this.cardsToApproveActive = false;
        this.familyLinksToApproveActive = false;
        this.residencesToApproveActive = false;
        this.triggerSearch();
    }

    public openMoreFiltersModal(): void {
        // Regenerate options with current language translations
        const currentCardStatusOptions = [
            { label: this.translateService.instant('common.all'), value: '' },
            { label: this.translateService.instant('pages.userRecords.card.status.ACTIVE'), value: CardStatus.ACTIVE },
            { label: this.translateService.instant('pages.userRecords.card.status.ACTIVATING'), value: CardStatus.ACTIVATING },
            { label: this.translateService.instant('pages.userRecords.card.status.PROCESSING'), value: CardStatus.PROCESSING },
            { label: this.translateService.instant('pages.userRecords.card.status.EXPIRED'), value: CardStatus.EXPIRED },
        ];

        const currentRoleOptions = [{ label: this.translateService.instant('common.all'), value: '' }, ...this.roleOptions];

        const currentConfirmedOptions = [
            { label: this.translateService.instant('common.all'), value: '' },
            { label: this.translateService.instant('common.yes'), value: true },
            { label: this.translateService.instant('common.no'), value: false },
        ];

        const dialogRef = this.dialog.open(MoreFiltersDialogComponent, {
            width: '672px',
            data: {
                cardStatusOptions: currentCardStatusOptions,
                roleOptions: currentRoleOptions,
                confirmedOptions: currentConfirmedOptions,
                currentFilters: this.moreFiltersData,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap((result) => {
                    // The dialog only owns cardStatus/role/confirmed/address fields and returns a fresh
                    // object, so re-apply the quick-filter toggles it knows nothing about.
                    this.moreFiltersData = { ...result, ...this.buildQuickFilterData() };
                    this.triggerSearch();
                }),
            )
            .subscribe();
    }

    private buildQuickFilterData(): { [key: string]: string } {
        return {
            ...(this.cardsToApproveActive ? { pendingCardApproval: 'true' } : {}),
            ...(this.familyLinksToApproveActive ? { familyStatus: 'false' } : {}),
            ...(this.residencesToApproveActive ? { residenceStatus: 'PENDING' } : {}),
        };
    }

    public fetchPendingCounts(): void {
        this.userService
            .getPendingRequestCounts()
            .pipe(
                takeUntil(this.destroy$),
                tap((result) => {
                    this.pendingCardsCount = result?.pendingCardRequests || 0;
                    this.pendingFamilyLinksCount = result?.pendingFamilyLinkRequests || 0;
                    this.pendingResidencesCount = result?.pendingResidences || 0;
                }),
            )
            .subscribe();
    }

    public pageChange($event): void {
        this.pageSubject.next($event);
    }

    private triggerSearch(): void {
        // Save quick filters state to store for persistence across navigation
        this.store.dispatch(
            SetUserSearchQuickFilters({
                cardsToApproveActive: this.cardsToApproveActive,
                familyLinksToApproveActive: this.familyLinksToApproveActive,
                residencesToApproveActive: this.residencesToApproveActive,
                moreFiltersData: this.moreFiltersData,
            }),
        );
        this.search('AdvancedSearch');
    }

    public search(activeForm: string): void {
        this.loading = true;
        this.store
            .select(userSearchPageOptionsSelector)
            .pipe(
                take(1),
                tap((options) => {
                    this.store.dispatch(
                        SetUserSearchPageOptionsAction({
                            pageIndex: 0,
                            itemsPerPage: options.itemsPerPage,
                        }),
                    );
                }),
            )
            .subscribe();

        if (activeForm === AvailableUserSearchForms.advancedSearch) {
            const formValues = this.advancedSearchForm.value;
            const combinedValues = { ...formValues, ...this.moreFiltersData };

            // Explicitly add familyStatus when family links filter is active
            if (this.familyLinksToApproveActive) {
                combinedValues.familyStatus = 'false';
            }

            of(combinedValues)
                .pipe(
                    take(1),
                    tap((values) =>
                        this.store.dispatch(
                            SetUserSearchAdvancedForm({
                                advancedSearchForm: {
                                    firstName: values.firstName || '',
                                    lastName: values.lastName || '',
                                    email: values.email || '',
                                    birthDate: values.birthDate ? `${formatDateOnly(values.birthDate)}T00:00:00.000Z` : '',
                                    phone: values.phone || '',
                                    role: values.role?.value ?? (typeof values.role === 'string' ? values.role : ''),
                                    confirmed: values.confirmed?.value !== undefined ? values.confirmed.value : typeof values.confirmed === 'boolean' ? values.confirmed : '',
                                    number: values.number || '',
                                    streetName: values.streetName || '',
                                    postCode: values.postCode || '',
                                    cardStatus: values.cardStatus?.value ?? (typeof values.cardStatus === 'string' ? values.cardStatus : ''),
                                    cardCode: values.cardCode || '',
                                    familyStatus: values.familyStatus || '',
                                    residenceStatus: values.residenceStatus || '',
                                    pendingCardApproval: values.pendingCardApproval || '',
                                },
                            }),
                        ),
                    ),
                )
                .subscribe();

            this.store.dispatch(SetUserSearchActiveForm({ activeForm: AvailableUserSearchForms.advancedSearch }));
        } else {
            of(this.quickSearchForm.value)
                .pipe(
                    take(1),
                    tap((values) =>
                        this.store.dispatch(
                            SetUserSearchQuickForm({
                                quickSearchForm: { search: values.search },
                            }),
                        ),
                    ),
                )
                .subscribe();

            this.store.dispatch(SetUserSearchActiveForm({ activeForm: AvailableUserSearchForms.quickSearch }));
        }

        this.store.dispatch(SearchUsersAction());
    }

    private fetchTranslations(): Observable<Record<string, any>> {
        return this.translateService.get(['pages.userRecords', 'common']).pipe(
            takeUntil(this.destroy$),
            share(),
            map((t) => ({ ...t.common, ...t['pages.userRecords'] })),
            tap((_t) => {
                this.t = _t;
            }),
        );
    }

    private setupTableColumn(): void {
        this.userColumns = [
            { key: 'name', label: this.translateService.instant('pages.userRecords.columns.name') },
            { key: 'email', label: this.translateService.instant('pages.userRecords.columns.email') },
            { key: 'cardNumber', label: this.translateService.instant('pages.userRecords.columns.cardNumber') },
            { key: 'familyLinks', label: this.translateService.instant('pages.userRecords.columns.familyLinks') },
            { key: 'residences', label: this.translateService.instant('pages.userRecords.columns.residences') },
            { key: 'emailConfirmed', label: this.translateService.instant('pages.userRecords.columns.emailConfirmed') },
            { key: 'action', label: '' },
        ];
    }

    private setupTotalRecordSub(): void {
        this.store
            .select(userSearchTotalSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((total) => (this.userLength = total)),
            )
            .subscribe();
    }

    private setupPageOptionsSub(): void {
        this.store
            .select(userSearchPageOptionsSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((options) => {
                    if (options.pageIndex !== undefined || options.itemsPerPage !== undefined) {
                        return;
                    }
                    this.store.dispatch(
                        SetUserSearchPageOptionsAction({
                            pageIndex: 0,
                            itemsPerPage: 10,
                        }),
                    );
                }),
                tap((options) => (this.pageOptions = options)),
            )
            .subscribe();
    }

    private setupUsersSub(): void {
        this.fetchTranslations()
            .pipe(
                takeUntil(this.destroy$),
                mergeMap(() => this.store.select(userSearchUsersSelector)),
                filter((users) => !!users),
                map((users) => users.map((u) => this.userUtil.getTransformedUser(u))),
                tap((users) => (this.users = users)),
                tap(() => (this.loading = false)),
                tap(() => this.fetchResidencesForUsers()),
            )
            .subscribe();
    }

    private fetchResidencesForUsers(): void {
        this.residenceService
            .getAllResidences()
            .pipe(
                take(1),
                tap((residences) => {
                    this.residenceMap.clear();
                    residences.forEach((r) => {
                        if (!this.residenceMap.has(r.userId)) {
                            this.residenceMap.set(r.userId, []);
                        }
                        this.residenceMap.get(r.userId).push(r);
                    });
                }),
            )
            .subscribe();
    }

    private setupPageSubjectSub(): void {
        this.pageSubject
            .pipe(
                takeUntil(this.destroy$),
                tap((o) => {
                    this.loading = true;
                    this.store.dispatch(
                        SetUserSearchPageOptionsAction({
                            pageIndex: o.pageIndex,
                            itemsPerPage: o.pageSize,
                        }),
                    );
                    this.store.dispatch(SearchUsersAction());
                }),
            )
            .subscribe();
    }

    private setupForm(): void {
        this.quickSearchForm = this.formBuilder.group(
            {
                search: new FormControl('', []),
            },
            { updateOn: 'blur' },
        );

        this.advancedSearchForm = this.formBuilder.group({
            firstName: new FormControl('', []),
            lastName: new FormControl('', []),
            email: new FormControl('', []),
            birthDate: new FormControl('', []),
            phone: new FormControl('', []),
            role: new FormControl('', []),
            confirmed: new FormControl('', []),
            number: new FormControl('', []),
            streetName: new FormControl('', []),
            postCode: new FormControl('', []),
            cardStatus: new FormControl('', []),
            cardCode: new FormControl('', []),
        });

        // Subscribe to form value changes for debounced search
        this.advancedSearchForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => this.formValueChange$.next(values));
    }

    private removeHiddenFields() {
        const controls = Object.keys(this.advancedSearchForm.controls).slice(0, 5);
        controls.forEach((controlName) => {
            this.hiddenFields.forEach((hiddenField) => {
                if (controlName.toUpperCase() === (hiddenField as string) && this.advancedSearchForm.get(controlName)) {
                    this.advancedSearchForm.removeControl(controlName);
                }
            });
        });
        this.advancedSearchForm.updateValueAndValidity();
    }

    public displayField(hiddenField: ProfileField): boolean {
        return !this.hiddenFields.includes(hiddenField);
    }

    private setupDropdowns(): void {
        this.roleOptions.push({ label: this.t.all, value: '' });
        Object.keys(Roles).forEach((role) => {
            this.roleOptions.push({
                label: this.t[role],
                value: role,
            });
        });

        // Only include: All status, Active, Processing, Expired
        this.cardStatusOptions = [
            { label: this.t?.all || 'All', value: '' },
            { label: this.t?.card?.status?.ACTIVE || 'Active', value: CardStatus.ACTIVE },
            { label: this.t?.card?.status?.PROCESSING || 'Processing', value: CardStatus.PROCESSING },
            { label: this.t?.card?.status?.EXPIRED || 'Expired', value: CardStatus.EXPIRED },
        ];

        this.confirmedOptions = [
            { label: this.t?.all || 'All', value: '' },
            { label: this.t?.yes || 'Yes', value: true },
            { label: this.t?.no || 'No', value: false },
        ];
    }

    public exportDataTable(): void {
        const options = {
            fieldSeparator: ',',
            quoteStrings: '"',
            headers: [this.t.columns.name, this.t.columns.email, this.t.columns.cardNumber, this.t.columns.emailConfirmed],
        };

        this.store
            .select(userSearchActiveFormSelector)
            .pipe(
                take(1),
                switchMap((form) =>
                    iif(
                        () => form === AvailableUserSearchForms.quickSearch,
                        this.store.select(userSearchQuickFormSelector).pipe(
                            take(1),
                            switchMap((r) => this.userService.quickSearch(r.search, 1, this.maxExportResults)),
                        ),
                        this.store.select(userSearchAdvancedFormSelector).pipe(
                            take(1),
                            switchMap((r) => this.userService.advancedSearch(r, 1, this.maxExportResults)),
                        ),
                    ),
                ),
                tap((result) => {
                    this.searchResultsToExport = this.formatExportData(result);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars, new-cap
                    const exportedData = new ngxCsv(this.searchResultsToExport, 'userRecords', options);
                }),
            )
            .subscribe();
    }

    private formatExportData(result): Record<string, any>[] {
        return result.docs.map((u) => ({
            name: `${u.firstName || ''} ${u.lastName || ''}`,
            email: u.email,
            cardNumber: u.cardNumber,
            emailConfirmed: u.confirmed ? this.t.yes : this.t.no,
        }));
    }
}
